import { ArrowUpRight } from "lucide-react";

import { LocaleLink } from "./LocaleLink";
import { Reveal } from "./Reveal";
import { useT } from "@/lib/i18n-dict";
import { FEATURE_NAV, INDUSTRY_NAV } from "@/content/site";
import type { NavItem } from "@/content/site";

type Group = { title: string; items: NavItem[] };

const FEATURES: NavItem[] = FEATURE_NAV;
const INDUSTRIES: NavItem[] = INDUSTRY_NAV.filter((item) => item.to !== "/industries");

const NEXT_STEPS: NavItem[] = [
  { label: "Plans and prices", to: "/prices" },
  { label: "All industries", to: "/industries" },
  { label: "Find your solution", to: "/solution-finder" },
  { label: "Book a demo", to: "/registration-demo" },
];

/**
 * Contextual internal links between modules, industries and pricing.
 * Helps visitors (and crawlers) move between the site's commercial pages.
 */
export function RelatedLinks({
  variant,
  currentPath,
}: {
  variant: "feature" | "industry" | "prices";
  currentPath?: string;
}) {
  const t = useT();

  const groups: Group[] =
    variant === "feature"
      ? [
          { title: "Other capabilities", items: FEATURES.filter((i) => i.to !== currentPath) },
          { title: "Solutions by industry", items: INDUSTRIES },
          { title: "Next steps", items: NEXT_STEPS },
        ]
      : variant === "industry"
        ? [
            { title: "Capabilities you will use", items: FEATURES },
            { title: "Other industries", items: INDUSTRIES.filter((i) => i.to !== currentPath) },
            { title: "Next steps", items: NEXT_STEPS },
          ]
        : [
            { title: "What is included", items: FEATURES },
            { title: "Solutions by industry", items: INDUSTRIES },
            { title: "Next steps", items: NEXT_STEPS.filter((i) => i.to !== "/prices") },
          ];

  return (
    <section className="border-t border-border/60 bg-surface/40 py-14">
      <div className="container-page">
        <h2 className="font-display text-xl font-semibold">{t("Explore more")}</h2>
        <div className="mt-8 grid gap-10 md:grid-cols-3">
          {groups.map((group, index) => (
            <Reveal key={group.title} delay={index * 60}>
              <p className="text-xs font-semibold tracking-[0.18em] text-primary-ink uppercase">
                {t(group.title)}
              </p>
              <ul className="mt-4 grid gap-2.5">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <LocaleLink
                      to={item.to}
                      className="group inline-flex items-start gap-1.5 text-sm leading-relaxed text-foreground/90 underline-offset-4 hover:text-primary-ink hover:underline"
                    >
                      {t(item.label)}
                      <ArrowUpRight
                        className="mt-0.5 size-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                        aria-hidden
                      />
                    </LocaleLink>
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
